<template>
  <!-- Android PaymentInfoDialog (booking): confirm an outside-platform payment (cash / check / other) or revise an Atwood total -->
  <div ref="modal" class="modal fade fxModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static"
    data-bs-keyboard="false">
    <div class="modal-dialog modal-dialog-centered" style="max-width: 480px">
      <div class="modal-content">
        <div class="modal-body p-[20px] font-roboto">
          <FxModalHeader title="Payment Acknowledgement" :dismiss="false" @close="close" />

          <!-- inline confirmation (the global $dialog would stack under this Bootstrap modal) -->
          <template v-if="confirming">
            <p class="m-0 mt-[20px] text-center text-[16px] font-medium text-[#333333]">{{ confirmTitle }}</p>
            <p class="m-0 mt-[8px] text-center text-[14px] leading-[20px] text-[#333333]">{{ confirmText }}</p>
            <div class="flex justify-center gap-[10px] mt-[20px]">
              <button type="button" class="fxDeclineCancel" :disabled="busy" @click="confirming = null">No</button>
              <button type="button" class="fxBtnPrimary" :disabled="busy" @click="runConfirmed">Yes</button>
            </div>
          </template>

          <template v-else-if="payment">
            <p class="m-0 mt-[15px] text-[14px] leading-[20px] text-[#333333]">{{ description }}</p>

            <div class="flex items-center justify-between mt-[15px] p-[10px] rounded-[10px] border-1 border-[#EFEFEF]">
              <span class="text-[13px] text-[#666666]">Payment Type</span>
              <span class="text-[14px] font-medium text-[#333333]">{{ typeLabel }}</span>
            </div>
            <div class="flex items-center justify-between mt-[8px] p-[10px] rounded-[10px] border-1 border-[#EFEFEF]">
              <span class="text-[13px] text-[#666666]">Amount</span>
              <span class="text-[14px] font-medium text-[#333333]">{{ payment.amount_prefix || payment.amount || '-' }}</span>
            </div>

            <div v-if="type === 'check' && checks.length" class="grid grid-cols-2 gap-[10px] mt-[15px]">
              <a v-for="img in checks" :key="img.key || img.url" :href="img.url" target="_blank" rel="noopener"
                class="block rounded-[10px] overflow-hidden border-1 border-[#EFEFEF]">
                <img :src="img.url" :alt="img.key || 'check'" class="w-full h-[110px] object-cover">
                <span class="block px-2 py-1 text-[12px] text-[#666666]">{{ img.key === 'back_image' ? 'Back' : 'Front' }}</span>
              </a>
            </div>

            <div v-if="type === 'other'" class="mt-[15px] flex flex-col gap-[8px]">
              <div class="p-[10px] rounded-[10px] border-1 border-[#EFEFEF]">
                <span class="block text-[12px] text-[#666666]">Institution Name</span>
                <span class="text-[14px] text-[#333333]">{{ payment.institution_name || '-' }}</span>
              </div>
              <div class="p-[10px] rounded-[10px] border-1 border-[#EFEFEF]">
                <span class="block text-[12px] text-[#666666]">Notes</span>
                <span class="text-[14px] text-[#333333]">{{ payment.description || '-' }}</span>
              </div>
            </div>

            <label v-if="isAtwood" class="block m-0 mt-[15px]">
              <span class="block mb-[5px] text-[13px] font-medium text-[#333333]">Revised Job Total ($)</span>
              <input v-model="revisedAmount" type="number" min="0" step="0.01"
                class="w-full px-[12px] py-[10px] rounded-[10px] border-1 outline-none text-[14px] text-[#333333]"
                :class="amountError ? 'border-[#F67280] bg-[#FEF7F7]' : 'border-[#EFEFEF]'">
              <span v-if="amountError" class="block mt-[4px] text-[12px] text-[#EA8A8A]">{{ amountError }}</span>
            </label>

            <div class="flex justify-center gap-[10px] mt-[20px]">
              <template v-if="isAtwood">
                <button type="button" class="fxBtnPrimary" @click="askAtwood">Proceed with Atwood</button>
              </template>
              <template v-else>
                <button type="button" class="fxDeclineCancel" @click="askDecision('no')">Not Received</button>
                <button type="button" class="fxBtnPrimary" @click="askDecision('yes')">Received</button>
              </template>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import { bookingActionsApi } from "../../services/bookingActionsApi";

const capitalize = (s) => String(s || "").replace(/\b\w/g, (c) => c.toUpperCase());

export default {
  components: { FxModalHeader },
  props: {
    bookingId: { type: [String, Number], required: true },
    consumerName: { type: String, default: "" },
  },
  emits: ["done", "failed"],
  data() {
    return { payment: null, revisedAmount: "", amountError: "", confirming: null, busy: false };
  },
  computed: {
    type() {
      return String(this.payment?.payment_type || "").toLowerCase();
    },
    isAtwood() {
      return this.type === "atwood";
    },
    typeLabel() {
      return capitalize(this.payment?.payment_type) || "-";
    },
    checks() {
      return (this.payment?.check || []).filter((c) => c && c.url);
    },
    // same wording as Android PaymentInfoDialog
    description() {
      const name = this.consumerName || "The consumer";
      const amount = this.payment?.amount_prefix || "";
      if (this.type === "check") return `${name} has selected check as the preferred payment option. Please confirm if you have received a check for sum of ${amount} and the images attached, are of the same check as received.`;
      if (this.type === "cash") return `${name} has selected Cash as the preferred payment option. Please confirm if you have received the sum of ${amount}`;
      if (this.isAtwood) return `${name} has selected Atwood as the preferred payment option. Please enter the revised job total and hit Proceed to update the invoice.`;
      if (this.type === "other") return `${name} has selected Other as the preferred payment option. Please review the details below and acknowledge if you want to proceed.`;
      return `${name} has selected ${this.typeLabel} as the preferred payment option. Please confirm if you have received the sum of ${amount}`;
    },
    confirmTitle() {
      return this.confirming?.title || "";
    },
    confirmText() {
      return this.confirming?.text || "";
    },
  },
  beforeUnmount() {
    Modal.getInstance(this.$refs.modal)?.dispose();
  },
  methods: {
    // loads GET payment/booking/data/{id}; resolves false when there is nothing to acknowledge
    async open({ silentEmpty = false } = {}) {
      const res = await bookingActionsApi.paymentData(this.bookingId);
      if (!res.ok) {
        if (!silentEmpty) this.$emit("failed", res);
        return false;
      }
      if (!res.body?.payment_type) {
        if (!silentEmpty) this.$emit("failed", { ok: false, message: "No payment is waiting for acknowledgement." });
        return false;
      }
      this.payment = res.body;
      this.revisedAmount = res.body.amount != null ? String(res.body.amount) : "";
      this.amountError = "";
      this.confirming = null;
      Modal.getOrCreateInstance(this.$refs.modal).show();
      return true;
    },
    close() {
      this.confirming = null;
      Modal.getInstance(this.$refs.modal)?.hide();
    },
    askDecision(decision) {
      this.confirming = {
        title: `Payment via ${this.typeLabel}`,
        text: "Do you want to proceed?",
        run: () => bookingActionsApi.paymentDecision(this.bookingId, decision),
      };
    },
    askAtwood() {
      const amount = parseFloat(String(this.revisedAmount).replace(/[$,]/g, ""));
      if (Number.isNaN(amount) || amount < 0) {
        this.amountError = "Please enter a valid amount.";
        return;
      }
      this.amountError = "";
      this.confirming = {
        title: "Payment via Atwood",
        text: "The revised total can not be changed later. If you wish to edit the revise total, then hit NO, otherwise hit YES to proceed.",
        run: () => bookingActionsApi.atwood(this.bookingId, amount, this.payment.event),
      };
    },
    async runConfirmed() {
      if (!this.confirming) return;
      this.busy = true;
      const res = await this.confirming.run();
      this.busy = false;
      this.close();
      // Android refreshes the job on success and on failure alike
      if (res.ok) this.$emit("done", res);
      else this.$emit("failed", res);
    },
  },
};
</script>
